import { Dispatch, SetStateAction } from 'react';
import { useLocalStorage as useLocalStorageHook } from 'usehooks-ts';

import { ContentType } from '@models/Content';
import { getInitStorageValue } from '@utils/storageUtils';

export const getFavoriteTagsLocalStorageKey = (contentType: ContentType) =>
  `FAVORITE_${contentType.toUpperCase()}_TAGS`;

export const getCrudTableSkeletonCountLocalStorageKey = (contentType: ContentType) =>
  `${contentType.toUpperCase()}_SKELETON_COUNT`;

export const localStorageContext = {
  browserIdentifier: 'BROWSER_IDENTIFIER',
  collectionPreviewHeight: 'COLLECTION_PREVIEW_HEIGHT',
  collectionPreviewMode: 'COLLECTION_PREVIEW_MODE',
  country: 'COUNTRY',
  fetchTitleErrorCount: 'FETCH_TITLE_ERROR_COUNT',
  globalSearchContext: 'GLOBAL_SEARCH_CONTEXT',
  providerImageViewMode: 'PROVIDER_IMAGE_VIEW_MODE',
  searchLanguage: 'SEARCH_LANGUAGE',
  searchViewMode: 'SEARCH_VIEW_MODE',
  theme: 'THEME',
  titleSearchImageViewMode: 'TITLE_SEARCH_IMAGE_VIEW_MODE',
};

export type LocalStorageContext = keyof typeof localStorageContext;

/**
 * Generic hook that extends the `useLocalStorage` hook from `usehooks-ts`
 * @param context Key or function that returns the key to be used in localStorage
 * @param defaultValue Default value if no value is stored in localStorage
 * @param transformValue Function to transform result based on localStorage value
 */
export const useLocalStorage = <T, K = T>(
  context: (() => string) | LocalStorageContext,
  defaultValue?: T,
  transformValue?: (value: T) => K
): [K, Dispatch<SetStateAction<T>>, VoidFunction] => {
  const key = typeof context === 'string' ? localStorageContext[context] : context();

  const [value, setValue, removeValue] = useLocalStorageHook<T>(
    key,
    getInitStorageValue(key) ?? defaultValue ?? (undefined as T)
  );

  return [transformValue?.(value) ?? (value as unknown as K), setValue, removeValue];
};
